import axios from "axios";
import { useMutation, useQuery } from "react-query";
import {
  ImportResponse,
  UploadFileResponse,
  ValidationResponse,
  AdvancedValidateResponse,
  FixAddressesRequest,
  AddRoutesRequest,
  MappingData,
} from "../models";

const API_PATH = "SalesOrders/";

export const useGetMappings = () =>
  useQuery<MappingData, Error>(
    "field-mappings",
    () =>
      axios
        .get(`${API_PATH}GetImportFieldMappings`)
        .then((res) => res.data)
        .catch((error) => {
          console.error("Failed to fetch mappings:", error);
          throw error;
        }),
    {
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30, // Cache for 30 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

export const useUploadFile = () =>
  useMutation<UploadFileResponse, Error, FormData>((formData) =>
    axios
      .post(`${API_PATH}UploadFile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // Increase timeout for large file uploads - 5 minutes
        timeout: 300000,
      })
      .then((res) => {
        if (!res.data.success) {
          return {
            success: false,
            errors: res.data.errors || [],
          };
        }
        return {
          success: true,
          importSessionId: res.data.importSessionId || "",
        };
      })
      .catch((error) => {
        console.error("Upload failed:", error);

        // Handle timeout errors specifically
        if (error.code === "ECONNABORTED") {
          return {
            success: false,
            errors: [
              "The request timed out. Your file may be too large or the server is busy. Please try again or use a smaller file.",
            ],
          };
        }

        return {
          success: false,
          errors: error.response?.data?.errors || [
            "An error occurred during upload",
          ],
        };
      })
  );

export const useBasicValidate = () =>
  useMutation<ValidationResponse, Error, string>((importSessionId) =>
    axios
      .post(
        `${API_PATH}BasicValidate`,
        { importSessionId },
        {
          timeout: 300000, // 5 minutes
        }
      )
      .then((res) => res.data)
      .catch((error) => {
        console.error("Basic validation failed:", error);
        return {
          success: false,
          errors: error.response?.data?.errors || [
            "An error occurred during basic validation",
          ],
        };
      })
  );

export const useAdvancedValidate = () =>
  useMutation<AdvancedValidateResponse, Error, string>((importSessionId) =>
    axios
      .post(
        `${API_PATH}AdvancedValidate`,
        { importSessionId },
        {
          timeout: 300000, // 5 minutes
        }
      )
      .then((res) => res.data)
      .catch((error) => {
        console.error("Advanced validation failed:", error);
        return {
          success: false,
          errors: error.response?.data?.errors || [
            "An error occurred during advanced validation",
          ],
        };
      })
  );

export const useFixAddresses = () =>
  useMutation<ValidationResponse, Error, FixAddressesRequest>(
    (fixAddressesRequest) =>
      axios
        .post(`${API_PATH}FixAddresses`, fixAddressesRequest, {
          timeout: 300000, // 5 minutes
        })
        .then((res) => res.data)
        .catch((error) => {
          console.error("Fix addresses failed:", error);
          return {
            success: false,
            errors: error.response?.data?.errors || [
              "An error occurred during fix addresses",
            ],
          };
        })
  );

export const useAddRoutes = () =>
  useMutation<ValidationResponse, Error, AddRoutesRequest>((addRoutesRequest) =>
    axios
      .post(`${API_PATH}AddNewRoutes`, addRoutesRequest, {
        timeout: 300000, // 5 minutes
      })
      .then((res) => res.data)
      .catch((error) => {
        console.error("Add routes failed:", error);
        return {
          success: false,
          errors: error.response?.data?.errors || [
            "An error occurred during add routes",
          ],
        };
      })
  );

export const useImportOrders = () =>
  useMutation<ImportResponse, Error, string>((importSessionId) =>
    axios
      .post(
        `${API_PATH}Import`,
        { importSessionId },
        {
          timeout: 3600000, // 1 hour
        }
      )
      .then((res) => res.data)
      .catch((error) => {
        console.error("Import failed:", error);
        return {
          success: false,
          errors: error.response?.data?.errors || [
            "An error occurred during import",
          ],
        };
      })
  );
